import React, { createContext, useState } from 'react';
import {
	ICancelInfo,
	ICartOrderInfo,
	ICountry,
	IFuelTypes,
	ILabel,
	IOffer,
	IOfferContext,
	IOrderInfo,
	ITollReservationInfo,
	ITollSectionOffer,
	Locales,
} from '../models';

interface IOfferProviderProps {
	children: React.ReactNode;
}

export const OfferContext = createContext<IOfferContext>({
	allOffers: [],
	offer: undefined,
	orderInfo: undefined,
	orderList: [],
	cancelledOrderInfo: undefined,
	vignetteOffers: [],
	dkvOffers: [],
	allFuels: undefined,
	allCountries: undefined,
	tollSectionOffers: [],
	cancelFormData: undefined,
	labels: [],
	lang: undefined,
	updateLang: () => {},
	updateLabels: () => {},
	updateAllOffers: () => {},
	updateOffer: () => {},
	resetOffer: () => {},
	updateVignetteOffers: () => {},
	updateDkvOffers: () => {},
	updateOrderInfo: () => {},
	updateOrderList: () => {},
	updateCancelledOrderInfo: () => {},
	updateAllFuels: () => {},
	updateAllCountries: () => {},
	updateTollSectionOffers: () => {},
	updateCancelFormData: () => {},
});

OfferContext.displayName = 'OfferContext';

export const OfferProvider = ({ children }: IOfferProviderProps) => {
	const [labels, setLabels] = useState<ILabel[]>([]);
	const [lang, setLang] = useState<Locales | undefined>(undefined);
	const [allOffers, setAllOffers] = useState<IOffer[]>([]);
	const [offer, setOffer] = useState<IOffer | undefined>(undefined);
	const [vignetteOffers, setVignetteOffers] = useState<IOffer[]>([]);
	const [dkvOffers, setDkvOffers] = useState<IOffer[]>([]);
	const [orderInfo, setOrderInfo] = useState<ITollReservationInfo | ICartOrderInfo | undefined>(undefined);
	const [orderList, setOrderList] = useState<IOrderInfo[]>([]);
	const [cancelledOrderInfo, setCancelledOrderInfo] = useState<IOrderInfo | undefined>(undefined);
	const [fuels, setFuels] = useState<IFuelTypes[] | undefined>(undefined);
	const [countries, setCountries] = useState<ICountry[] | undefined>(undefined);
	const [tollSectionOffers, setTollSectionOffers] = useState<ITollSectionOffer[]>([]);
	const [cancelFormData, setCancelFormData] = useState<ICancelInfo | undefined>(undefined);

	const updateAllOffers = (newOffers: IOffer[]) => {
		setAllOffers(newOffers);
	};

	const updateOffer = (newOffer?: IOffer) => {
		setOffer(newOffer);
	};

	const resetOffer = () => {
		setOffer(undefined);
	};

	const updateVignetteOffers = (newOffer: IOffer[]) => {
		setVignetteOffers(newOffer);
	};

	const updateDkvOffers = (newOffer: IOffer[]) => {
		setDkvOffers(newOffer);
	};

	const updateOrderInfo = (newOrderInfo: ITollReservationInfo | ICartOrderInfo) => {
		setOrderInfo(newOrderInfo);
	};

	const updateOrderList = (newOrderList: IOrderInfo[]) => {
		setOrderList(newOrderList);
	};

	const updateCancelledOrderInfo = (newCancelledOrderInfo: IOrderInfo) => {
		setCancelledOrderInfo(newCancelledOrderInfo);
	};

	const updateAllFuels = (newFuels: IFuelTypes[]) => {
		setFuels(newFuels);
	};

	const updateAllCountries = (newCountries: ICountry[]) => {
		setCountries(newCountries);
	};

	const updateTollSectionOffers = (newTollRouteOffers: ITollSectionOffer[]) => {
		setTollSectionOffers(newTollRouteOffers);
	};

	const updateLabels = (newLabels: ILabel[]) => {
		setLabels(newLabels);
	};

	const updateCancelFormData = (currCancelFormData: ICancelInfo) => {
		setCancelFormData(currCancelFormData);
	};

	const updateLang = (newLang?: Locales) => {
		setLang(newLang);
	};

	return (
		<OfferContext.Provider
			value={{
				allOffers,
				offer,
				orderInfo,
				orderList,
				cancelledOrderInfo,
				vignetteOffers,
				dkvOffers,
				allFuels: fuels,
				allCountries: countries,
				tollSectionOffers,
				labels,
				cancelFormData,
				lang,
				updateLang,
				updateLabels,
				updateAllOffers,
				updateOffer,
				resetOffer,
				updateOrderInfo,
				updateOrderList,
				updateCancelledOrderInfo,
				updateVignetteOffers,
				updateDkvOffers,
				updateAllFuels,
				updateAllCountries,
				updateTollSectionOffers,
				updateCancelFormData,
			}}>
			{children}
		</OfferContext.Provider>
	);
};
